import React, { ReactNode, useState } from 'react'
import classNames from 'classnames'

import { COLOR_SCHEMES } from '../../utils/constants/colorSchemes'
import { IProps } from './index'

export const ContentSwitcher = ({
  colorScheme,
  switchPosition,
  labelA,
  labelB,
  bodyA,
  bodyB,
}: IProps): React.ReactElement => {
  const [currentBody, setCurrentBody] = useState('bodyA')

  const handleSwitch = (targetedBody?: string) => {
    const checkedBody = currentBody === 'bodyA' ? 'bodyB' : 'bodyA'
    setCurrentBody(targetedBody || checkedBody)
  }

  const switchClasses = classNames('content-switcher__switch', {
    [`content-switcher__switch--${switchPosition}`]: switchPosition,
  })

  const bodyAClasses = classNames('content-switcher__body-a', {
    'is-active': currentBody === 'bodyA',
  })
  const bodyBClasses = classNames('content-switcher__body-b', {
    'is-active': currentBody === 'bodyB',
  })

  const colorSchemeA = colorScheme !== 'None' ? colorScheme.split('/')[0] : null
  const colorSchemeB = colorScheme !== 'None' ? colorScheme.split('/')[1] : null

  const textColorA = colorSchemeA ? COLOR_SCHEMES[colorScheme][colorSchemeA].textColor : null
  const backgroundColorA = colorSchemeA
    ? COLOR_SCHEMES[colorScheme][colorSchemeA].backgroundColor
    : null

  const textColorB = colorSchemeB ? COLOR_SCHEMES[colorScheme][colorSchemeB].textColor : null
  const backgroundColorB = colorSchemeB
    ? COLOR_SCHEMES[colorScheme][colorSchemeB].backgroundColor
    : null

  return (
    <>
      <div className="content-switcher" data-testid="content-switcher">
        <div className={switchClasses}>
          <input
            type="checkbox"
            checked={currentBody === 'bodyA' ? false : true}
            id="content-switcher"
            className="content-switcher__checkbox"
            onChange={() => handleSwitch()}
          />
          <button className="content-switcher__label-a" onClick={() => handleSwitch('bodyA')}>
            {labelA}
          </button>
          <label
            htmlFor="content-switcher"
            className="content-switcher__toggle"
          >{`Switch content`}</label>
          <button className="content-switcher__label-b" onClick={() => handleSwitch('bodyB')}>
            {labelB}
          </button>
        </div>

        {bodyA && (
          <div className={bodyAClasses}>
            {React.Children.map(
              bodyA,
              (item: ReactNode) => item && React.isValidElement(item) && React.cloneElement(item),
            )}
          </div>
        )}

        {bodyB && (
          <div className={bodyBClasses}>
            {React.Children.map(
              bodyB,
              (item: ReactNode) => item && React.isValidElement(item) && React.cloneElement(item),
            )}
          </div>
        )}
      </div>

      <style jsx>{`
        .content-switcher {
          display: grid;
          justify-items: center;
          align-items: center;
          position: relative;
          padding-top: ${switchPosition === 'top-center-outside' ? '5.875rem' : '0'};
          padding-bottom: ${switchPosition === 'bottom-center-outside' ? '5.875rem' : '0'};

          &__switch {
            display: flex;
            position: absolute;
            padding: 0.75rem 1.5rem;
            border-radius: 6rem;
            box-shadow: inset 0 0 0 1px var(--gray);
            background-color: var(--white);
            z-index: 2;

            &[class*='-left'] {
              left: calc(100% / 14);
            }

            &[class*='-center'] {
              left: 50%;
              transform: translateX(-50%);
            }

            &[class*='-right'] {
              right: calc(100% / 14);
            }

            &[class*='--top-'] {
              top: 1.25rem;

              &[class*='-outside'] {
                top: 1.5rem;
              }
            }

            &[class*='--middle-'] {
              top: 50%;
              transform: translateY(-50%);
            }

            &[class*='--bottom-'] {
              bottom: 1.25rem;

              &[class*='-outside'] {
                bottom: 1.5rem;
              }
            }

            &.content-switcher__switch--middle-center {
              transform: translate(-50%, -50%);
            }
          }

          &__checkbox {
            display: none;
          }

          &__toggle {
            display: inline-block;
            width: 2rem;
            height: 1.25rem;
            border-radius: 1rem;
            position: relative;
            background-color: var(--slate);
            text-indent: -9999px;
            cursor: pointer;

            &::before {
              content: '';
              position: absolute;
              height: 1rem;
              width: 1rem;
              top: 0.125rem;
              left: 0.125rem;
              border-radius: 50%;
              background-color: var(--white);
              transition: 0.4s;
            }
          }

          &__label-a,
          &__label-b {
            opacity: 1;
            transition: color 0.4s, opacity 0.4s;
            text-transform: uppercase;
            font-size: 0.875rem;
            font-weight: 700;
            cursor: pointer;
          }

          &__label-a {
            margin-right: 0.75rem;
          }

          &__label-b {
            margin-left: 0.75rem;
            opacity: 0.4;
          }

          &__checkbox:checked ~ &__toggle {
            &::before {
              left: 0.875rem;
            }
          }

          &__checkbox:checked ~ &__label-a {
            opacity: 0.4;
          }

          &__checkbox:checked ~ &__label-b {
            opacity: 1;
          }

          &__body-a,
          &__body-b {
            grid-column: 1;
            grid-row: 1;
            transition: visibility 0s 0.4s, opacity 0s 0.4s;
          }

          &__body-a.is-active,
          &__body-b.is-active {
            transition: opacity 0.4s;
            z-index: 1;
          }

          &__body-a {
            --text-color: ${textColorA ? textColorA : 'inherit'};
            --background-color: ${backgroundColorA ? backgroundColorA : 'inherit'};
            visibility: ${currentBody === 'bodyA' ? 'visible' : 'hidden'};
            opacity: ${currentBody === 'bodyA' ? '1' : '0'};
          }

          &__body-b {
            --text-color: ${textColorB ? textColorB : 'inherit'};
            --background-color: ${backgroundColorB ? backgroundColorB : 'inherit'};
            visibility: ${currentBody === 'bodyB' ? 'visible' : 'hidden'};
            opacity: ${currentBody === 'bodyB' ? '1' : '0'};
          }
        }
      `}</style>
    </>
  )
}
